import React, { useState, Fragment } from 'react';
import { Dialog, Transition, Combobox } from '@headlessui/react';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { UserIcon } from '@heroicons/react/24/outline';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import { INDUSTRY_OPTIONS, COMPANY_SIZE_OPTIONS, getCompanySizeByName } from '../../../../constants/companyData';
import { useTheme } from '../../../../context/ThemeContext';

const AddCompanyModal = ({ isOpen, onClose, onCompanyAdded, userId, zIndex = 20 }) => {
  const { isDarkMode } = useTheme();
  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    website: '',
    industries: [], // Changed from industry (string) to industries (array)
    size: '',
    description: '',
    otherIndustry: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Industry query state
  const [industryQuery, setIndustryQuery] = useState('');
  
  // Company size query state
  const [sizeQuery, setSizeQuery] = useState('');
  
  // Filter industries based on the query
  const filteredIndustries = industryQuery === ''
    ? INDUSTRY_OPTIONS
    : INDUSTRY_OPTIONS.filter(industry => 
        industry.name.toLowerCase().includes(industryQuery.toLowerCase())
      );
      
  // Filter company sizes based on the query
  const filteredSizes = sizeQuery === ''
    ? COMPANY_SIZE_OPTIONS
    : COMPANY_SIZE_OPTIONS.filter(sizeOption =>
        sizeOption.name.toLowerCase().includes(sizeQuery.toLowerCase()) ||
        sizeOption.shortName.toLowerCase().includes(sizeQuery.toLowerCase())
      );
  
  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    
    setCompanyFormData({
      ...companyFormData,
      [name]: value
    });
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: ''
      });
    }
  };
  
  // Handle industry selection
  const handleSelectIndustry = (industry) => {
    // Don't add duplicates
    if (!companyFormData.industries.includes(industry.name)) {
      setCompanyFormData({
        ...companyFormData,
        industries: [...companyFormData.industries, industry.name]
      });
    }
    
    // Clear industry query after selection
    setIndustryQuery('');
    
    // Clear any industry-related errors
    if (formErrors.industries) {
      setFormErrors({
        ...formErrors,
        industries: ''
      });
    }
  };
  
  // Handle removing an industry
  const handleRemoveIndustry = (industry) => {
    setCompanyFormData({
      ...companyFormData,
      industries: companyFormData.industries.filter(ind => ind !== industry)
    });
  };
  
  // Validate the form before submission
  const validateForm = () => {
    const errors = {};
    
    if (!companyFormData.name.trim()) {
      errors.name = 'Company name is required';
    }
    
    if (companyFormData.website && !/^https?:\/\/.+/i.test(companyFormData.website)) {
      errors.website = 'Please enter a valid URL starting with http:// or https://';
    }
    
    if (companyFormData.industries.length === 0) {
      errors.industries = 'At least one industry is required';
    }
    
    if (!companyFormData.size) {
      errors.size = 'Company size is required';
    }
    
    if (companyFormData.description.trim() && companyFormData.description.trim().length < 50) {
      errors.description = 'Description must be at least 50 characters if provided';
    } else if (companyFormData.description.trim().length > 2000) {
      errors.description = 'Description must be less than 2000 characters';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSaveCompany = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const db = getFirestore();
      
      // Format the company data
      const companyData = {
        name: companyFormData.name.trim(),
        website: companyFormData.website.trim(),
        industries: companyFormData.industries, // Save as array
        primaryIndustry: companyFormData.industries[0] || '', // First industry as primary for backward compatibility
        size: companyFormData.size,
        description: companyFormData.description.trim(),
        createdByUserId: userId || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      // Add the company to Firestore companies collection
      const companiesCollectionRef = collection(db, 'companies');
      const companyRef = await addDoc(companiesCollectionRef, companyData);
      
      // Get the new company ID
      const companyId = companyRef.id;
      
      // Call the callback function with the new company data
      if (onCompanyAdded) {
        onCompanyAdded({
          id: companyId,
          ...companyData,
          industry: companyData.primaryIndustry, // For backward compatibility
          createdAt: new Date().toISOString(), // Convert Firestore timestamp to string for immediate use
          updatedAt: new Date().toISOString()
        });
      }
      
      // Reset form after successful submission
      setCompanyFormData({
        name: '',
        website: '',
        industries: [],
        size: '',
        description: '',
        otherIndustry: ''
      });
      
      setIsSubmitting(false);
    } catch (error) {
      console.error('Error adding company:', error);
      toast.error('Failed to add company. Please try again.');
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0" style={{ zIndex, pointerEvents: isOpen ? 'auto' : 'none' }}>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog 
          as="div" 
          className="fixed inset-0 overflow-y-auto" 
          style={{ zIndex }}
          onClose={onClose}
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-gray-900 dark:bg-opacity-80" />
          <div className="fixed inset-0 flex items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                
                <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-[70vh] overflow-y-auto">
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900 dark:text-white">
                      Add New Company
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Enter the details of the company you want to add.
                    </p>
                  </div>
                  
                  <div className="mt-6 space-y-6">
                    <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      {/* Company Name */}
                      <div className="sm:col-span-6">
                        <label htmlFor="company-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Company Name <span className="text-red-500">*</span>
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            name="name"
                            id="company-name"
                            value={companyFormData.name}
                            onChange={handleFormChange}
                            className={`block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-purple-500 focus:ring-purple-500 placeholder:text-gray-400 dark:placeholder:text-gray-500 sm:text-sm ${
                              formErrors.name ? 'border-red-500' : ''
                            }`}
                            placeholder="e.g., Acme Corporation"
                          />
                          {formErrors.name && (
                            <p className="mt-1 text-sm text-red-500">{formErrors.name}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Company Website */}
                      <div className="sm:col-span-6">
                        <label htmlFor="company-website" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Company Website (Optional, but recommended)
                        </label>
                        <div className="mt-1">
                          <input
                            type="url"
                            name="website"
                            id="company-website"
                            value={companyFormData.website}
                            onChange={handleFormChange}
                            className={`block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-purple-500 focus:ring-purple-500 placeholder:text-gray-400 dark:placeholder:text-gray-500 sm:text-sm ${
                              formErrors.website ? 'border-red-500' : ''
                            }`}
                            placeholder="e.g., https://www.example.com"
                          />
                          {formErrors.website && (
                            <p className="mt-1 text-sm text-red-500">{formErrors.website}</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Industry Multi-Select */}
                      <div className="sm:col-span-3">
                        <Combobox as="div" onChange={handleSelectIndustry}>
                          <label htmlFor="company-industry" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Industry <span className="text-red-500">*</span>
                          </label>
                          <div className="relative mt-1">
                            <div className={`block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus-within:border-purple-500 focus-within:ring-purple-500 ${
                              formErrors.industries ? 'border-red-500' : ''
                            }`}>
                              <div className="flex flex-wrap gap-1 p-1">
                                {/* Selected Industries Pills */}
                                {companyFormData.industries.map((industryName) => {
                                  const industry = INDUSTRY_OPTIONS.find(ind => ind.name === industryName) || { name: industryName, shortName: industryName };
                                  return (
                                    <span 
                                      key={industry.id || industryName}
                                      className="inline-flex items-center gap-x-1 rounded-md bg-purple-100 px-2 py-1 text-sm font-medium text-purple-700"
                                    >
                                      <span>{industry.shortName}</span>
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          handleRemoveIndustry(industryName);
                                        }}
                                        className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full hover:bg-purple-200 hover:text-purple-900 focus:bg-purple-500 focus:text-white focus:outline-none"
                                      >
                                        <span className="sr-only">Remove {industryName}</span>
                                        <XMarkIcon className="h-3 w-3" />
                                      </button>
                                    </span>
                                  );
                                })}
                                <Combobox.Input
                                  className="flex-grow min-w-[100px] border-none py-1 pl-2 text-sm leading-5 text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                  displayValue={() => industryQuery}
                                  onChange={(event) => setIndustryQuery(event.target.value)}
                                  placeholder={companyFormData.industries.length > 0 ? "Add another industry..." : "Select industry..."}
                                  onClick={(e) => e.target.select()}
                                />
                              </div>
                            </div>
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                            </Combobox.Button>

                            {filteredIndustries.length > 0 && (
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 focus:outline-none sm:text-sm">
                                {filteredIndustries.map((industry) => (
                                  <Combobox.Option
                                    key={industry.id}
                                    value={industry}
                                    className={({ active }) =>
                                      `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                        active ? 'bg-purple-600 text-white' : 'text-gray-900 dark:text-gray-100'
                                      }`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <div className="flex">
                                          <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                            {industry.name}
                                          </span>
                                          <span className={`ml-2 truncate ${active ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                                            ({industry.shortName})
                                          </span>
                                        </div>
                                        {companyFormData.industries.includes(industry.name) && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-purple-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            )}
                          </div>
                          {formErrors.industries && (
                            <p className="mt-1 text-sm text-red-500">{formErrors.industries}</p>
                          )}
                        </Combobox>
                      </div>
                      
                      {/* Company Size */}
                      <div className="sm:col-span-3">
                        <Combobox as="div" value={companyFormData.size} onChange={(sizeOption) => {
                          setCompanyFormData({
                            ...companyFormData,
                            size: sizeOption.name
                          });
                          if (formErrors.size) {
                            setFormErrors({
                              ...formErrors,
                              size: ''
                            });
                          }
                          
                          // Reset size query after selection
                          setSizeQuery('');
                        }}>
                          <label htmlFor="company-size" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Company Size <span className="text-red-500">*</span>
                          </label>
                          <div className="relative mt-1">
                            <div className={`block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus-within:border-purple-500 focus-within:ring-purple-500 ${
                              formErrors.size ? 'border-red-500' : ''
                            }`}>
                              <div className="flex flex-wrap gap-1 p-1">
                                {companyFormData.size ? (
                                  <span className="inline-flex items-center gap-x-1 rounded-md bg-purple-100 px-2 py-1 text-sm font-medium text-purple-700">
                                    <UserIcon className="h-4 w-4 text-purple-500 mr-1" />
                                    {getCompanySizeByName(companyFormData.size)?.shortName || companyFormData.size}
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setCompanyFormData({
                                          ...companyFormData,
                                          size: ''
                                        });
                                      }}
                                      className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full hover:bg-purple-200 hover:text-purple-900 focus:bg-purple-500 focus:text-white focus:outline-none"
                                    >
                                      <span className="sr-only">Remove size</span>
                                      <XMarkIcon className="h-3 w-3" />
                                    </button>
                                  </span>
                                ) : null}
                                <Combobox.Input
                                  className="flex-grow min-w-[100px] border-none py-1 pl-2 text-sm leading-5 text-gray-900 dark:text-gray-100 bg-transparent focus:outline-none focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                                  displayValue={() => sizeQuery}
                                  onChange={(event) => setSizeQuery(event.target.value)}
                                  placeholder={companyFormData.size ? "" : "Select company size..."}
                                  onClick={(e) => e.target.select()}
                                />
                              </div>
                            </div>
                            <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                              <ChevronUpDownIcon className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                            </Combobox.Button>

                            {filteredSizes.length > 0 && (
                              <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-gray-600 focus:outline-none sm:text-sm">
                                {filteredSizes.map((sizeOption) => (
                                  <Combobox.Option
                                    key={sizeOption.id}
                                    value={sizeOption}
                                    className={({ active }) =>
                                      `relative cursor-default select-none py-2 pl-3 pr-9 ${
                                        active ? 'bg-purple-600 text-white' : 'text-gray-900 dark:text-gray-100'
                                      }`
                                    }
                                  >
                                    {({ active, selected }) => (
                                      <>
                                        <div className="flex items-center">
                                          <UserIcon className={`h-4 w-4 mr-2 ${active ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
                                          <span className={`truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                            {sizeOption.name}
                                          </span>
                                          <span className={`ml-2 truncate ${active ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`}>
                                            ({sizeOption.shortName})
                                          </span>
                                        </div>
                                        
                                        {companyFormData.size === sizeOption.name && (
                                          <span className={`absolute inset-y-0 right-0 flex items-center pr-4 ${active ? 'text-white' : 'text-purple-600'}`}>
                                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                          </span>
                                        )}
                                      </>
                                    )}
                                  </Combobox.Option>
                                ))}
                              </Combobox.Options>
                            )}
                          </div>
                          {formErrors.size && (
                            <p className="mt-1 text-sm text-red-500">{formErrors.size}</p>
                          )}
                        </Combobox>
                      </div>
                      
                      {/* Company Description */}
                      <div className="sm:col-span-6">
                        <label htmlFor="company-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          About the Company
                        </label>
                        <div className="mt-1">
                          <textarea
                            id="company-description"
                            name="description"
                            rows={4}
                            value={companyFormData.description}
                            onChange={handleFormChange}
                            className={`block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm focus:border-purple-500 focus:ring-purple-500 placeholder:text-gray-400 dark:placeholder:text-gray-500 sm:text-sm ${
                              formErrors.description ? 'border-red-500' : ''
                            }`}
                            placeholder="Briefly describe the company's mission, what they do, their main products/services, and their market."
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Min 50 characters, max 2000 characters. ({companyFormData.description.length}/2000)
                          </p>
                          {formErrors.description && (
                            <p className="mt-1 text-sm text-red-500">{formErrors.description}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 flex justify-end space-x-3 border-t border-gray-200 dark:border-gray-700 pt-6">
                    <button
                      type="button"
                      className="py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
                      onClick={onClose}
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-75"
                      onClick={handleSaveCompany}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Saving...' : 'Save Company'}
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default AddCompanyModal; 